import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, FileText } from 'lucide-react';
type Status = 'Completed' | 'No Content' | 'Workers Signing';
interface Record {
  id: string;
  fileType: 'pdf' | 'missing';
  fileName: string;
  site: string;
  subName: string;
  date: string;
  status: Status;
}
const SITES = ['1760 3rd Ave', '1760 Jerome Ave', 'Court Square LIC', '99 Claremont Ave', '251 Douglass Street', '450 Park Avenue South', '520 Fifth Avenue'];
const SUBS = ['Aurora Paint Corp.', 'Skytrac', 'Flushing Iron Weld', 'Sector Security', 'Luxintec', 'V10 Construction', 'Kitchens Of USA', 'Otis Elevator Company', 'Sunkeeper Solar', 'Architectural Flooring Resource Inc'];
function generateMockData(): Record[] {
  const data: Record[] = [];
  let id = 1;
  const add = (date: string, status: Status, subIdx: number, siteIdx: number) => {
    const isCompleted = status === 'Completed';
    data.push({
      id: String(id++),
      fileType: isCompleted ? 'pdf' : status === 'Workers Signing' ? 'pdf' : 'missing',
      fileName: isCompleted ? 'Signed PTP' : '',
      site: SITES[siteIdx % SITES.length],
      subName: SUBS[subIdx % SUBS.length],
      date,
      status
    });
  };
  // Today (03/15)
  for (let i = 0; i < 5; i++) add('03/15/2026', 'No Content', i, i);
  for (let i = 0; i < 3; i++) add('03/15/2026', 'Workers Signing', i + 2, i + 1);
  for (let i = 0; i < 10; i++) add('03/15/2026', 'Completed', i + 4, i + 2);
  // Last 7 days (03/09 - 03/14)
  for (let i = 0; i < 15; i++) add('03/12/2026', 'No Content', i + 1, i + 3);
  for (let i = 0; i < 10; i++) add('03/11/2026', 'Workers Signing', i + 3, i + 4);
  for (let i = 0; i < 30; i++) add('03/10/2026', 'Completed', i + 5, i + 5);
  return data;
}
const MOCK_DATA = generateMockData();
export function PTPPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [periodLabel, setPeriodLabel] = useState<string | null>(null);
  const [siteFilter, setSiteFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: null,
    end: null
  });
  useEffect(() => {
    const period = searchParams.get('period');
    const status = searchParams.get('status');
    const site = searchParams.get('site');
    if (status === 'incomplete') {
      setStatusFilter('Incomplete');
    } else {
      setStatusFilter(null);
    }
    if (site) {
      setSiteFilter(site);
    } else {
      setSiteFilter(null);
    }
    if (period === 'today') {
      setPeriodLabel('Today');
      setDateRange({
        start: '03/15/2026',
        end: '03/15/2026'
      });
    } else if (period === 'week') {
      setPeriodLabel('Last 7 days');
      setDateRange({
        start: '03/09/2026',
        end: '03/15/2026'
      });
    } else {
      setPeriodLabel(null);
      setDateRange({
        start: null,
        end: null
      });
    }
  }, [searchParams]);
  const clearAllFilters = () => {
    setStatusFilter(null);
    setPeriodLabel(null);
    setSiteFilter(null);
    setDateRange({
      start: null,
      end: null
    });
    setSearchParams({});
  };
  const removeFilter = (key: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    setSearchParams(newParams);
  };
  const filteredData = useMemo(() => {
    return MOCK_DATA.filter(item => {
      if (searchQuery && !item.subName.toLowerCase().includes(searchQuery.toLowerCase()) && !item.site.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (statusFilter === 'Incomplete') {
        if (item.status === 'Completed') return false;
      } else if (statusFilter && item.status !== statusFilter) {
        return false;
      }
      if (siteFilter && item.site !== siteFilter) {
        return false;
      }
      if (dateRange.start && item.date < dateRange.start) return false;
      if (dateRange.end && item.date > dateRange.end) return false;
      return true;
    });
  }, [searchQuery, statusFilter, dateRange, siteFilter]);
  const hasActiveFilters = statusFilter !== null || dateRange.start !== null || siteFilter !== null;
  return <div className="min-h-screen bg-[#fafafa] font-sans flex flex-col" data-id="element-4399">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4" data-id="element-4400">
        <div className="flex items-center gap-2" data-id="element-4401">
          <div className="text-blue-600 font-bold text-2xl tracking-tighter flex items-center" data-id="element-4402">
            CHST
            <div className="w-6 h-6 border-4 border-blue-600 rounded-full mx-0.5 relative" data-id="element-4403">
              <div className="absolute inset-0 m-auto w-2 h-2 bg-blue-600 rounded-full" data-id="element-4404"></div>
            </div>
          </div>
          <div className="text-[10px] font-bold text-blue-600 tracking-widest absolute mt-8 ml-1" data-id="element-4405">
            L D E R S
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 ml-4" data-id="element-4406">PTP</h1>
      </div>

      <main className="flex-1 p-6 max-w-[1600px] mx-auto w-full" data-id="element-4407">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-4408">
          <h2 className="text-xl text-gray-700" data-id="element-4409">
            Found{' '}
            <span className="font-semibold text-gray-900" data-id="element-4410">
              {filteredData.length}
            </span>{' '}
            PTPs
          </h2>

          <div className="flex items-center gap-3" data-id="element-4411">
            <div className="relative" data-id="element-4412">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-4413" />
              <input type="text" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4414" />
            </div>
            <button className="relative flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 bg-white transition-colors shadow-sm" data-id="element-4415">
              <SlidersHorizontal size={16} data-id="element-4416" />
              Filters
              {hasActiveFilters && <span className="absolute top-0 right-0 -mt-1 -mr-1 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" data-id="element-4417"></span>}
            </button>
          </div>
        </div>

        {hasActiveFilters && <div className="flex items-center flex-wrap gap-2 mb-6" data-id="element-4418">
            {siteFilter && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4419">
                <span className="text-gray-300" data-id="element-4420">Site:</span>
                <span className="font-medium" data-id="element-4421">{siteFilter}</span>
                <button onClick={() => removeFilter('site')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4422">
                  <X size={14} data-id="element-4423" />
                </button>
              </div>}

            {statusFilter && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4424">
                <span className="text-gray-300" data-id="element-4425">Status:</span>
                <span className="font-medium" data-id="element-4426">{statusFilter}</span>
                <button onClick={() => removeFilter('status')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4427">
                  <X size={14} data-id="element-4428" />
                </button>
              </div>}

            {periodLabel && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4429">
                <span className="text-gray-300" data-id="element-4430">Period:</span>
                <span className="font-medium" data-id="element-4431">{periodLabel}</span>
                <span className="text-gray-400 text-xs" data-id="element-4432">
                  ({dateRange.start} – {dateRange.end})
                </span>
                <button onClick={() => removeFilter('period')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4433">
                  <X size={14} data-id="element-4434" />
                </button>
              </div>}

            <button onClick={clearAllFilters} className="text-sm text-gray-500 hover:text-gray-900 underline underline-offset-2 ml-2 transition-colors" data-id="element-4435">
              Clear all
            </button>
          </div>}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-4436">
          <div className="overflow-x-auto" data-id="element-4437">
            <table className="w-full text-left text-sm" data-id="element-4438">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-semibold text-xs tracking-wider" data-id="element-4439">
                <tr data-id="element-4440">
                  <th className="px-6 py-4" data-id="element-4441">FILE</th>
                  <th className="px-6 py-4" data-id="element-4442">SITE</th>
                  <th className="px-6 py-4" data-id="element-4443">SUB NAME</th>
                  <th className="px-6 py-4" data-id="element-4444">DATE</th>
                  <th className="px-6 py-4" data-id="element-4445">STATUS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-4446">
                {filteredData.map(row => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-4447">
                    <td className="px-6 py-4" data-id="element-4448">
                      <div className="flex items-center gap-3" data-id="element-4449">
                        {row.fileType === 'pdf' ? <div className="relative" data-id="element-4450">
                            <FileText size={24} className="text-gray-300" data-id="element-4451" />
                            <div className="absolute bottom-0 left-0 bg-red-600 text-white text-[8px] font-bold px-1 rounded-sm" data-id="element-4452">
                              PDF
                            </div>
                          </div> : <div className="relative" data-id="element-4453">
                            <FileText size={24} className="text-gray-200" data-id="element-4454" />
                            <div className="absolute bottom-0 left-0 bg-gray-500 text-white text-[8px] font-bold px-1 rounded-sm" data-id="element-4455">
                              ?
                            </div>
                          </div>}
                        <span className="text-gray-900" data-id="element-4456">{row.fileName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4457">{row.site}</td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4458">{row.subName}</td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4459">{row.date}</td>
                    <td className="px-6 py-4" data-id="element-4460">
                      {row.status === 'Completed' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-4461">
                          Completed
                        </span>}
                      {row.status === 'No Content' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700" data-id="element-4462">
                          No Content
                        </span>}
                      {row.status === 'Workers Signing' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-4463">
                          Workers Signing
                        </span>}
                    </td>
                  </tr>)}
                {filteredData.length === 0 && <tr data-id="element-4464">
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-4465">
                      No PTPs found matching your criteria.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>;
}